import { CdoToAllDisMap, US_TERRITORY_COUNTRY_CODES } from '@onefootprint/core';
import type {
  CollectedDataOption,
  DataIdentifier,
  OnboardingRequirementCollectData,
  VaultData,
} from '@onefootprint/request-types';
import { getCollectDataAttributes, getCollectDataDis } from './get-collect-data-dis';

/**
 * Required DIs per CDO - these are the minimum DIs that must have values
 * for a CDO to be considered "collected"
 */
const CDO_TO_REQUIRED_DIS: Partial<Record<CollectedDataOption, DataIdentifier[]>> = {
  name: ['id.first_name', 'id.last_name'],
  dob: ['id.dob'],
  ssn4: ['id.ssn4'],
  ssn9: ['id.ssn9'],
  us_tax_id: ['id.us_tax_id'],
  full_address: ['id.address_line1', 'id.city', 'id.state', 'id.zip', 'id.country'],
  nationality: ['id.nationality'],
  us_legal_status: ['id.us_legal_status'],
  email: ['id.email'],
  phone_number: ['id.phone_number'],
};

/**
 * Returns default vault values for single-country configurations.
 * When a tenant restricts to one country and the vault doesn't have a country set,
 * this pre-populates it so RHF form-level defaultValues are correct from the start.
 */
export const getSingleCountryDefaults = (
  requirement: OnboardingRequirementCollectData,
  vault: VaultData,
): Partial<VaultData> => {
  const countries = requirement.residentialAddressCountries;
  if (countries?.length === 1 && !vault['id.country']) {
    return { 'id.country': countries[0] };
  }
  return {};
};

export const isCountryUsOrTerritories = (country: string | undefined): boolean => {
  if (!country) return true;
  return country === 'US' || US_TERRITORY_COUNTRY_CODES.includes(country);
};

const isVaultCountryUsOrTerritories = (vault: VaultData): boolean => {
  return isCountryUsOrTerritories(vault['id.country'] as string | undefined);
};

/**
 * Checks if the collect-data flow is applicable for this requirement.
 */
const isCollectDataApplicable = (requirement: OnboardingRequirementCollectData, vault: VaultData): boolean => {
  if (getCollectDataAttributes(requirement).length === 0) {
    return false;
  }

  // Check US-only condition
  return isVaultCountryUsOrTerritories(vault);
};

/**
 * Checks if the collect-data requirement has missing data that needs to be collected.
 */
export const hasMissingCollectData = (requirement: OnboardingRequirementCollectData, vault: VaultData): boolean => {
  if (!isCollectDataApplicable(requirement, vault)) {
    return false;
  }

  const mustCollect = [...requirement.missingAttributes, ...requirement.optionalAttributes];
  const requiredDis = getCollectDataAttributes(requirement)
    .filter(cdo => mustCollect.includes(cdo))
    .flatMap(cdo => CDO_TO_REQUIRED_DIS[cdo] ?? []);

  // Check if any required DI is missing a value
  return requiredDis.some(di => {
    const value = vault[di as keyof VaultData];
    return value === undefined || value === null || value === '';
  });
};

/**
 * The DIs to render read-only: those belonging to a CDO the playbook disables that already have a
 * value. The server can't narrow this for us - Verified prefill data isn't vaulted yet when the
 * requirement is generated - so `vault` must be the post-prefill data the form is initialized from.
 * An empty field stays editable even when disabled, or the user could never provide it.
 */
export const getDisabledDis = (requirement: OnboardingRequirementCollectData, vault: VaultData): DataIdentifier[] =>
  requirement.disabledCdos
    .flatMap(cdo => CdoToAllDisMap[cdo] ?? [])
    .filter(di => {
      const value = vault[di as keyof VaultData];
      return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';
    });

// CDOs the compact review can render read-only. A node collecting anything else (legal status,
// nationality, tax id) falls back to the editable form.
export const REVIEWABLE_ATTRIBUTES: CollectedDataOption[] = [
  'name',
  'dob',
  'email',
  'phone_number',
  'full_address',
  'ssn9',
  'ssn4',
];

/**
 * Whether to show the read-only compact review instead of the editable form: the playbook opts in
 * (`useCompactConfirm`), every collected field is read-only-renderable, and no required data is
 * still missing (Verified prefill or a returning user). `hasOverlay` (Footprint prefill challenge
 * or a step-up) forces the form.
 */
export const canShowCompactReview = (
  requirement: OnboardingRequirementCollectData,
  vault: VaultData,
  hasOverlay: boolean,
): boolean => {
  const collected = getCollectDataAttributes(requirement);
  return (
    requirement.useCompactConfirm &&
    !hasOverlay &&
    collected.length > 0 &&
    collected.every(cdo => REVIEWABLE_ATTRIBUTES.includes(cdo)) &&
    !hasMissingCollectData(requirement, vault)
  );
};

/**
 * Gets the vault data filtered to only the fields needed for the collect-data requirement.
 */
export const getCollectDataVaultData = (
  requirement: OnboardingRequirementCollectData,
  fullVault: VaultData,
): VaultData => {
  const fields = getCollectDataDis(requirement);
  return Object.fromEntries(fields.map(field => [field, fullVault[field as keyof VaultData]])) as VaultData;
};
